import { cn } from "@/lib/utils.ts";
import "highlight.js/styles/github-dark-dimmed.css";
import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { customMarkdownComponents, urnLinkTransform } from "./components.tsx";
import { MarkdownContext, type MarkdownContextType } from "./context.tsx";
import { remarkHtmlImages } from "./remarkHtmlImages.ts";
import { escapePipesInTables } from "./utils.ts";
/**
 * Renders Markdown content with custom components for specific link types
 * and interactions within the application context.
 */
export const Markdown = ({
  children,
  className,
  isStreaming,
  isTextStillArriving,
  message,
  untrusted,
  imageSrcs,
  shouldRenderHtmlImages,
  ...props
}: Omit<React.ComponentProps<typeof ReactMarkdown>, "children"> & {
  children: string;
  className?: string;
  isStreaming?: boolean;
  /** See {@link MarkdownContextType.message}. */
  message?: MarkdownContextType["message"];
  /**
   * Whether `children` is still growing. Defaults to `isStreaming`, which is the
   * conservative reading for a caller that hasn't distinguished the two — an
   * affordance is withheld rather than offered over partial text. A caller whose
   * `isStreaming` outlives the text arriving (a latched reveal, a
   * received-this-session flag) must pass this explicitly, or the affordance
   * never returns.
   */
  isTextStillArriving?: boolean;
  /** See {@link MarkdownContextType.untrusted}. */
  untrusted?: boolean;
  /** See {@link MarkdownContextType.imageSrcs}. */
  imageSrcs?: MarkdownContextType["imageSrcs"];
  /**
   * Render a lone `<img>` html node as an image rather than escaped text — a
   * document body, where MDXEditor saves resized images that way. Off for
   * everything else, which keeps showing raw html as text.
   */
  shouldRenderHtmlImages?: boolean;
}) => {
  // The custom link components read `isStreaming` off this context to animate
  // freshly streamed text. Memoized so consumers don't re-render whenever this
  // component re-renders.
  const contextValue = React.useMemo<MarkdownContextType>(
    () => ({
      isStreaming,
      isTextStillArriving: isTextStillArriving ?? isStreaming,
      message,
      untrusted,
      imageSrcs,
    }),
    [isStreaming, isTextStillArriving, message, untrusted, imageSrcs],
  );

  // Preprocess markdown to escape pipes in table cells
  const processedMarkdown = React.useMemo(() => {
    return escapePipesInTables(children);
  }, [children]);

  return (
    <MarkdownContext.Provider value={contextValue}>
      <div className={cn("markdown", className)}>
        <ReactMarkdown
          remarkPlugins={[
            remarkGfm,
            [remarkMath, { singleDollarTextMath: false }],
            ...(shouldRenderHtmlImages ? [remarkHtmlImages] : []),
          ]}
          rehypePlugins={[rehypeKatex, rehypeHighlight]}
          urlTransform={urnLinkTransform}
          components={customMarkdownComponents}
          {...props}
        >
          {processedMarkdown}
        </ReactMarkdown>
      </div>
    </MarkdownContext.Provider>
  );
};
